// Character detail view - displays full character information

import { css, html } from 'lit';
import { api, getCDNBaseUrl } from '../../lib/api.js';
import { BaseComponent } from '../../lib/base-component.js';
import { router } from '../../lib/router.js';

/**
 * @typedef {object} ClassFeature
 * @property {string} name
 * @property {string} [grantType]
 * @property {string} [choiceLabel]
 * @property {string} [description]
 * @property {Array<string>} [selections]
 */

/**
 * @typedef {object} BackgroundFeature
 * @property {string} name
 * @property {string} [description]
 * @property {Array<string>} [selections]
 */

/**
 * @typedef {object} LineageBenefit
 * @property {string} name
 * @property {string} [description]
 */

/**
 * @typedef {object} SpeciesLineageDetail
 * @property {string} name
 * @property {string} [description]
 * @property {Array<LineageBenefit>} [benefits]
 * @property {Array<string>} [selections]
 */

/**
 * @typedef {object} SpeciesTraitDetail
 * @property {string} name
 * @property {string} [description]
 * @property {Array<string>} [selections]
 */

/**
 * @typedef {object} CharacterDetail
 * @property {string} id
 * @property {string} name
 * @property {string} [tokenId]
 * @property {number} [level]
 * @property {string} [species]
 * @property {string} [class]
 * @property {string} [background]
 * @property {number} [hp]
 * @property {number} [maxHp]
 * @property {number} [ac]
 * @property {number} [speed]
 * @property {import('../../types/jsdoc-types.js').AbilityScores} [abilityScores]
 * @property {Array<string>} [saveProficiencies]
 * @property {Array<string>} [skillProficiencies]
 * @property {Array<string>} [armorProficiencies]
 * @property {Array<string>} [weaponProficiencies]
 * @property {Array<import('../../types/jsdoc-types.js').SkillBonus>} [skillBonuses]
 * @property {Array<ClassFeature>} [classFeatures]
 * @property {string} [backgroundDescription]
 * @property {Array<BackgroundFeature>} [backgroundFeatures]
 * @property {SpeciesLineageDetail} [speciesLineage]
 * @property {Array<SpeciesTraitDetail>} [speciesTraits]
 * @property {Array<string>} [spellsPrepared]
 * @property {Array<import('../../types/jsdoc-types.js').InventoryItem>} [inventory]
 */

/**
 * @typedef {object} TokenListItem
 * @property {string} slug
 * @property {string} [image_url]
 */

// SquareUserRound icon SVG
const userIconLarge = html`<svg
  xmlns="http://www.w3.org/2000/svg"
  width="48"
  height="48"
  viewBox="0 0 24 24"
  fill="none"
  stroke="currentColor"
  stroke-width="1.5"
  stroke-linecap="round"
  stroke-linejoin="round"
  class="text-white"
>
  <path d="M18 21a6 6 0 0 0-12 0" />
  <circle cx="12" cy="11" r="4" />
  <rect width="18" height="18" x="3" y="3" rx="2" />
</svg>`;

// Sword icon SVG
const swordIcon = html`<svg
  xmlns="http://www.w3.org/2000/svg"
  width="20"
  height="20"
  viewBox="0 0 24 24"
  fill="none"
  stroke="currentColor"
  stroke-width="2"
  stroke-linecap="round"
  stroke-linejoin="round"
  class="me-2"
>
  <polyline points="14.5 17.5 3 6 3 3 6 3 17.5 14.5" />
  <line x1="13" x2="19" y1="19" y2="13" />
  <line x1="16" x2="20" y1="16" y2="20" />
  <line x1="19" x2="21" y1="21" y2="19" />
</svg>`;

const swordIconLarge = html`<svg
  xmlns="http://www.w3.org/2000/svg"
  width="48"
  height="48"
  viewBox="0 0 24 24"
  fill="none"
  stroke="currentColor"
  stroke-width="2"
  stroke-linecap="round"
  stroke-linejoin="round"
  class="mb-2 opacity-50"
>
  <polyline points="14.5 17.5 3 6 3 3 6 3 17.5 14.5" />
  <line x1="13" x2="19" y1="19" y2="13" />
  <line x1="16" x2="20" y1="16" y2="20" />
  <line x1="19" x2="21" y1="21" y2="19" />
</svg>`;

// Shield icon SVG
const shieldIcon = html`<svg
  xmlns="http://www.w3.org/2000/svg"
  width="20"
  height="20"
  viewBox="0 0 24 24"
  fill="none"
  stroke="currentColor"
  stroke-width="2"
  stroke-linecap="round"
  stroke-linejoin="round"
  class="me-2"
>
  <path
    d="M20 13c0 5-3.5 7.5-7.66 8.95a1 1 0 0 1-.67-.01C7.5 20.5 4 18 4 13V6a1 1 0 0 1 1-1c2 0 4.5-1.2 6.24-2.72a1.17 1.17 0 0 1 1.52 0C14.51 3.81 17 5 19 5a1 1 0 0 1 1 1z"
  />
</svg>`;

// Sparkles icon SVG
const sparklesIcon = html`<svg
  xmlns="http://www.w3.org/2000/svg"
  width="20"
  height="20"
  viewBox="0 0 24 24"
  fill="none"
  stroke="currentColor"
  stroke-width="2"
  stroke-linecap="round"
  stroke-linejoin="round"
  class="me-2"
>
  <path
    d="m12 3-1.912 5.813a2 2 0 0 1-1.275 1.275L3 12l5.813 1.912a2 2 0 0 1 1.275 1.275L12 21l1.912-5.813a2 2 0 0 1 1.275-1.275L21 12l-5.813-1.912a2 2 0 0 1-1.275-1.275L12 3Z"
  />
  <path d="M5 3v4" />
  <path d="M19 17v4" />
  <path d="M3 5h4" />
  <path d="M17 19h4" />
</svg>`;

// BookOpen icon SVG
const bookOpenIcon = html`<svg
  xmlns="http://www.w3.org/2000/svg"
  width="20"
  height="20"
  viewBox="0 0 24 24"
  fill="none"
  stroke="currentColor"
  stroke-width="2"
  stroke-linecap="round"
  stroke-linejoin="round"
  class="me-2"
>
  <path d="M2 3h6a4 4 0 0 1 4 4v14a3 3 0 0 0-3-3H2z" />
  <path d="M22 3h-6a4 4 0 0 0-4 4v14a3 3 0 0 1 3-3h7z" />
</svg>`;

// Dna icon SVG
const dnaIcon = html`<svg
  xmlns="http://www.w3.org/2000/svg"
  width="20"
  height="20"
  viewBox="0 0 24 24"
  fill="none"
  stroke="currentColor"
  stroke-width="2"
  stroke-linecap="round"
  stroke-linejoin="round"
  class="me-2"
>
  <path d="M2 15c6.667-6 13.333 0 20-6" />
  <path d="M9 22c1.798-1.998 2.518-3.995 2.807-5.993" />
  <path d="M15 2c-1.798 1.998-2.518 3.995-2.807 5.993" />
  <path d="m17 6-2.5-2.5" />
  <path d="m14 8-1-1" />
  <path d="m7 18 2.5 2.5" />
  <path d="m3.5 14.5.5.5" />
  <path d="m20 9 .5.5" />
  <path d="m6.5 12.5 1 1" />
  <path d="m16.5 10.5 1 1" />
  <path d="m10 16 1.5 1.5" />
</svg>`;

/**
 * Character detail view component
 * Displays comprehensive character information including abilities, proficiencies,
 * class features, background, species traits, and equipment
 */
class CharacterDetailView extends BaseComponent {
  static properties = {
    characterId: { type: String, attribute: 'character-id' },
    _character: { type: Object, state: true },
    _loading: { type: Boolean, state: true },
    _error: { type: String, state: true },
  };

  static styles = css`
    :host {
      display: block;
    }
  `;

  constructor() {
    super();
    /** @type {string} */
    this.characterId = '';
    /** @type {CharacterDetail | null} */
    this._character = null;
    /** @type {boolean} */
    this._loading = true;
    /** @type {string | null} */
    this._error = null;
  }

  async connectedCallback() {
    await super.connectedCallback();
    await this._loadCharacter();
  }

  get _characterTokenImage() {
    if (!this._character || !this._character.tokenId || !getCDNBaseUrl()) return null;
    return `${getCDNBaseUrl()}/token/${this._character.tokenId}`;
  }

  get _sortedInventory() {
    if (!this._character || !this._character.inventory) return [];
    // Sort: equipped items first, then by type, then by name
    return [...this._character.inventory].sort((a, b) => {
      if (a.equipped && !b.equipped) return -1;
      if (!a.equipped && b.equipped) return 1;
      /** @type {Record<string, number>} */
      const typeOrder = { weapon: 0, armor: 1, item: 2 };
      const typeA = typeOrder[a.type] ?? 3;
      const typeB = typeOrder[b.type] ?? 3;
      if (typeA !== typeB) return typeA - typeB;
      return (a.name || '').localeCompare(b.name || '');
    });
  }

  async _loadCharacter() {
    if (!this.characterId) {
      this._error = 'Character ID is required';
      this._loading = false;
      return;
    }

    this._loading = true;
    this._error = null;

    try {
      this._character = /** @type {CharacterDetail} */ (await api.getCharacter(this.characterId));
      this.dispatchEvent(
        new CustomEvent('heading-update', { bubbles: true, composed: true, detail: this._character.name }),
      );
    } catch (err) {
      console.error('Failed to load character:', err);
      this._error = 'Failed to load character. Please try again.';
    } finally {
      this._loading = false;
    }
  }

  /** @param {number} score */
  _getModifierText(score) {
    const modifier = Math.floor((score - 10) / 2);
    return modifier >= 0 ? `+${modifier}` : `${modifier}`;
  }

  /** @param {string} ability */
  _formatAbilityName(ability) {
    /** @type {Record<string, string>} */
    const names = {
      str: 'Strength',
      dex: 'Dexterity',
      con: 'Constitution',
      int: 'Intelligence',
      wis: 'Wisdom',
      cha: 'Charisma',
    };
    return names[ability] || ability.toUpperCase();
  }

  /** @param {string} skill */
  _formatSkillName(skill) {
    return skill
      .split('-')
      .map((/** @type {string} */ word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  }

  /** @param {string} amount */
  _formatSkillBonusAmount(amount) {
    switch (amount) {
      case 'wisModMin1':
        return 'Wis mod (min +1)';
      default:
        return amount;
    }
  }

  /** @param {string} prof */
  _formatProficiencyName(prof) {
    return prof
      .split('-')
      .map((/** @type {string} */ word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  }

  /** @param {string} selection */
  _formatSelectionName(selection) {
    return selection
      .split('-')
      .map((/** @type {string} */ word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  }

  /** @param {ClassFeature} feature */
  _hasSelections(feature) {
    return feature.selections && feature.selections.length > 0;
  }

  /** @param {string} grantType */
  _getFeatureBadgeClass(grantType) {
    switch (grantType) {
      case 'weapon-mastery':
        return 'bg-warning text-dark';
      case 'feat-choice':
        return 'bg-info text-dark';
      case 'cantrip-choice':
      case 'spellcasting':
        return 'bg-purple';
      case 'expertise':
        return 'bg-success';
      case 'choice':
        return 'bg-primary';
      default:
        return 'bg-secondary';
    }
  }

  /** @param {string} type */
  _getItemTypeBadgeClass(type) {
    switch (type) {
      case 'weapon':
        return 'bg-danger';
      case 'armor':
        return 'bg-primary';
      default:
        return 'bg-secondary';
    }
  }

  /** @param {string} type */
  _formatItemType(type) {
    switch (type) {
      case 'weapon':
        return 'Weapon';
      case 'armor':
        return 'Armor';
      case 'item':
        return 'Item';
      default:
        return type || 'Item';
    }
  }

  /** @param {string} slot */
  _formatSlotName(slot) {
    /** @type {Record<string, string>} */
    const names = {
      mainHand: 'Main Hand',
      offHand: 'Off Hand',
      body: 'Body',
      head: 'Head',
      feet: 'Feet',
      hands: 'Hands',
    };
    return names[slot] || slot;
  }

  _goBack() {
    router.navigate('/characters');
  }

  render() {
    return html`
      ${
        this._loading
          ? html`
            <div class="text-center text-muted py-5">
              <div class="spinner-border" role="status">
                <span class="visually-hidden">Loading...</span>
              </div>
              <p class="mt-3">Loading character...</p>
            </div>
          `
          : this._error
            ? html` <div class="alert alert-danger" role="alert">${this._error}</div> `
            : this._character
              ? html`
                <!-- Header -->
                <div class="mb-4">
                  <div class="d-flex gap-3 align-items-start">
                    ${
                      this._characterTokenImage
                        ? html`
                          <div class="flex-shrink-0">
                            <img
                              src="${this._characterTokenImage}"
                              alt="Token"
                              class="rounded"
                              style="width: 96px; height: 96px; object-fit: cover;"
                            />
                          </div>
                        `
                        : html`
                          <div
                            class="flex-shrink-0 bg-secondary rounded d-flex align-items-center justify-content-center"
                            style="width: 96px; height: 96px;"
                          >
                            ${userIconLarge}
                          </div>
                        `
                    }
                    <div class="flex-grow-1">
                      <h1 class="h2 mb-1">${this._character.name}</h1>
                      <p class="text-muted mb-2">
                        Level ${this._character.level} ${this._character.class} - ${this._character.species} -
                        ${this._character.background}
                      </p>
                      <div class="d-flex gap-3">
                        <span class="badge bg-primary">HP: ${this._character.hp}/${this._character.maxHp}</span>
                        <span class="badge bg-secondary">AC: ${this._character.ac}</span>
                        <span class="badge bg-info">Speed: ${this._character.speed} ft</span>
                      </div>
                    </div>
                  </div>
                </div>

                <!-- Ability Scores -->
                <div class="card mb-4">
                  <div class="card-header">
                    <h5 class="mb-0">Ability Scores</h5>
                  </div>
                  <div class="card-body">
                    <div class="row g-3">
                      ${
                        /** @type {Array<keyof import('../../types/jsdoc-types.js').AbilityScores>} */ ([
                          'str',
                          'dex',
                          'con',
                          'int',
                          'wis',
                          'cha',
                        ]).map(
                          (ability) => html`
                            <div class="col-6 col-md-4 col-lg-2">
                              <div class="text-center p-3 border rounded">
                                <div class="text-muted small">${ability.toUpperCase()}</div>
                                <div class="fs-4 fw-bold">${this._character?.abilityScores?.[ability] || 10}</div>
                                <div class="text-muted small">
                                  ${this._getModifierText(this._character?.abilityScores?.[ability] || 10)}
                                </div>
                              </div>
                            </div>
                          `,
                        )
                      }
                    </div>
                  </div>
                </div>

                <!-- Proficiencies -->
                <div class="card mb-4">
                  <div class="card-header">
                    <h5 class="mb-0">${shieldIcon}Proficiencies</h5>
                  </div>
                  <div class="card-body">
                    <div class="row g-3">
                      <div class="col-12 col-md-6">
                        <h6 class="text-muted mb-2">Saving Throws</h6>
                        <div class="d-flex flex-wrap gap-1">
                          ${(this._character.saveProficiencies || []).map(
                            (save) => html` <span class="badge bg-primary">${this._formatAbilityName(save)}</span> `,
                          )}
                          ${
                            !this._character.saveProficiencies || this._character.saveProficiencies.length === 0
                              ? html` <span class="text-muted small">None</span> `
                              : ''
                          }
                        </div>
                      </div>
                      <div class="col-12 col-md-6">
                        <h6 class="text-muted mb-2">Skills</h6>
                        <div class="d-flex flex-wrap gap-1">
                          ${(this._character.skillProficiencies || []).map(
                            (skill) => html` <span class="badge bg-success">${this._formatSkillName(skill)}</span> `,
                          )}
                          ${
                            !this._character.skillProficiencies || this._character.skillProficiencies.length === 0
                              ? html` <span class="text-muted small">None</span> `
                              : ''
                          }
                        </div>
                      </div>
                      <div class="col-12 col-md-6">
                        <h6 class="text-muted mb-2">Armor</h6>
                        <div class="d-flex flex-wrap gap-1">
                          ${(this._character.armorProficiencies || []).map(
                            (armor) => html`
                              <span class="badge bg-secondary">${this._formatProficiencyName(armor)}</span>
                            `,
                          )}
                          ${
                            !this._character.armorProficiencies || this._character.armorProficiencies.length === 0
                              ? html` <span class="text-muted small">None</span> `
                              : ''
                          }
                        </div>
                      </div>
                      <div class="col-12 col-md-6">
                        <h6 class="text-muted mb-2">Weapons</h6>
                        <div class="d-flex flex-wrap gap-1">
                          ${(this._character.weaponProficiencies || []).map(
                            (weapon) => html`
                              <span class="badge bg-secondary">${this._formatProficiencyName(weapon)}</span>
                            `,
                          )}
                          ${
                            !this._character.weaponProficiencies || this._character.weaponProficiencies.length === 0
                              ? html` <span class="text-muted small">None</span> `
                              : ''
                          }
                        </div>
                      </div>
                      ${
                        this._character.skillBonuses && this._character.skillBonuses.length > 0
                          ? html`
                            <div class="col-12">
                              <h6 class="text-muted mb-2">Skill Bonuses</h6>
                              <div class="d-flex flex-wrap gap-1">
                                ${this._character.skillBonuses.map(
                                  (bonus) => html`
                                    <span class="badge bg-info text-dark">
                                      ${this._formatSkillName(bonus.skill)} +${this._formatSkillBonusAmount(bonus.amount)}
                                      <span class="ms-1 opacity-75">from ${this._formatSelectionName(bonus.source)}</span>
                                    </span>
                                  `,
                                )}
                              </div>
                            </div>
                          `
                          : ''
                      }
                    </div>
                  </div>
                </div>

                <!-- Class Features -->
                <div class="card mb-4">
                  <div class="card-header">
                    <h5 class="mb-0">${sparklesIcon}Class Features</h5>
                  </div>
                  <div class="card-body">
                    ${
                      !this._character.classFeatures || this._character.classFeatures.length === 0
                        ? html`
                          <div class="text-center text-muted py-3">
                            <p class="mb-0">No class features</p>
                          </div>
                        `
                        : html`
                          <div class="d-flex flex-column gap-3">
                            ${this._character.classFeatures.map(
                              (feature) => html`
                                <div class="border-bottom pb-3">
                                  <h6 class="mb-1">${feature.name}</h6>
                                  ${
                                    this._hasSelections(feature)
                                      ? html`
                                        ${
                                          feature.choiceLabel && feature.choiceLabel !== feature.name
                                            ? html` <div class="text-muted small mb-2">${feature.choiceLabel}</div> `
                                            : ''
                                        }
                                        <div class="d-flex flex-wrap gap-2 mt-2">
                                          ${(feature.selections || []).map(
                                            (/** @type {string} */ selection) => html`
                                              <span class="badge ${this._getFeatureBadgeClass(feature.grantType || '')}"
                                                >${this._formatSelectionName(selection)}</span
                                              >
                                            `,
                                          )}
                                        </div>
                                      `
                                      : feature.description
                                        ? html` <p class="small text-muted mb-0">${feature.description}</p> `
                                        : ''
                                  }
                                </div>
                              `,
                            )}
                          </div>
                        `
                    }
                  </div>
                </div>

                <!-- Background -->
                <div class="card mb-4">
                  <div class="card-header">
                    <h5 class="mb-0">${bookOpenIcon}Background: ${this._character.background}</h5>
                  </div>
                  <div class="card-body">
                    ${
                      this._character.backgroundDescription
                        ? html` <p class="text-muted mb-3">${this._character.backgroundDescription}</p> `
                        : ''
                    }
                    ${
                      this._character.backgroundFeatures && this._character.backgroundFeatures.length > 0
                        ? html`
                          <div class="d-flex flex-column gap-3">
                            ${this._character.backgroundFeatures.map(
                              (feature) => html`
                                <div class="border-bottom pb-3">
                                  <h6 class="mb-1">${feature.name}</h6>
                                  <p class="small text-muted mb-0">${feature.description}</p>
                                  ${
                                    feature.selections && feature.selections.length > 0
                                      ? html`
                                        <div class="d-flex flex-wrap gap-2 mt-2">
                                          ${feature.selections.map(
                                            (selection) => html`
                                              <span class="badge bg-info text-dark"
                                                >${this._formatSelectionName(selection)}</span
                                              >
                                            `,
                                          )}
                                        </div>
                                      `
                                      : ''
                                  }
                                </div>
                              `,
                            )}
                          </div>
                        `
                        : html`
                          <div class="text-center text-muted py-3">
                            <p class="mb-0">No background features</p>
                          </div>
                        `
                    }
                  </div>
                </div>

                <!-- Species Traits -->
                <div class="card mb-4">
                  <div class="card-header">
                    <h5 class="mb-0">${dnaIcon}Species Traits: ${this._character.species}</h5>
                  </div>
                  <div class="card-body">
                    ${
                      this._character.speciesLineage
                        ? html`
                          <div class="mb-3 pb-3 border-bottom">
                            <h6 class="mb-1">${this._character.speciesLineage.name}</h6>
                            <p class="small text-muted mb-2">${this._character.speciesLineage.description}</p>
                            ${
                              this._character.speciesLineage.benefits &&
                              this._character.speciesLineage.benefits.length > 0
                                ? html`
                                  <div class="ms-3">
                                    ${this._character.speciesLineage.benefits.map(
                                      (benefit) => html`
                                        <div class="mb-2">
                                          <div class="fw-medium small">${benefit.name}</div>
                                          <p class="small text-muted mb-0">${benefit.description}</p>
                                        </div>
                                      `,
                                    )}
                                  </div>
                                `
                                : ''
                            }
                            ${
                              this._character.speciesLineage.selections &&
                              this._character.speciesLineage.selections.length > 0
                                ? html`
                                  <div class="d-flex flex-wrap gap-2 mt-2">
                                    ${this._character.speciesLineage.selections.map(
                                      (selection) => html`
                                        <span class="badge bg-purple">${this._formatSelectionName(selection)}</span>
                                      `,
                                    )}
                                  </div>
                                `
                                : ''
                            }
                          </div>
                        `
                        : ''
                    }
                    ${
                      this._character.speciesTraits && this._character.speciesTraits.length > 0
                        ? html`
                          <div class="d-flex flex-column gap-3">
                            ${this._character.speciesTraits.map(
                              (trait) => html`
                                <div class="border-bottom pb-3">
                                  <h6 class="mb-1">${trait.name}</h6>
                                  <p class="small text-muted mb-0">${trait.description}</p>
                                  ${
                                    trait.selections && trait.selections.length > 0
                                      ? html`
                                        <div class="d-flex flex-wrap gap-2 mt-2">
                                          ${trait.selections.map(
                                            (selection) => html`
                                              <span class="badge bg-success"
                                                >${this._formatSelectionName(selection)}</span
                                              >
                                            `,
                                          )}
                                        </div>
                                      `
                                      : ''
                                  }
                                </div>
                              `,
                            )}
                          </div>
                        `
                        : !this._character.speciesLineage
                          ? html`
                            <div class="text-center text-muted py-3">
                              <p class="mb-0">No species traits</p>
                            </div>
                          `
                          : ''
                    }
                  </div>
                </div>

                <!-- Spells Prepared -->
                ${
                  this._character.spellsPrepared && this._character.spellsPrepared.length > 0
                    ? html`
                      <div class="card mb-4">
                        <div class="card-header">
                          <h5 class="mb-0">${sparklesIcon}Spells Prepared</h5>
                        </div>
                        <div class="card-body">
                          <div class="d-flex flex-wrap gap-2">
                            ${this._character.spellsPrepared.map(
                              (spell) => html`
                                <span class="badge bg-purple">${this._formatSelectionName(spell)}</span>
                              `,
                            )}
                          </div>
                        </div>
                      </div>
                    `
                    : ''
                }

                <!-- Equipment -->
                <div class="card mb-4">
                  <div class="card-header">
                    <h5 class="mb-0">${swordIcon}Equipment</h5>
                  </div>
                  <div class="card-body p-0">
                    ${
                      !this._character.inventory || this._character.inventory.length === 0
                        ? html`
                          <div class="text-center text-muted py-4">
                            ${swordIconLarge}
                            <p class="mb-0">No equipment yet</p>
                          </div>
                        `
                        : html`
                          <table class="table table-hover mb-0">
                            <thead>
                              <tr>
                                <th>Name</th>
                                <th>Type</th>
                                <th>Details</th>
                                <th>Status</th>
                                <th class="text-end">Qty</th>
                              </tr>
                            </thead>
                            <tbody>
                              ${this._sortedInventory.map(
                                (item) => html`
                                  <tr>
                                    <td class="${item.equipped ? 'fw-semibold' : ''}">${item.name}</td>
                                    <td>
                                      <span class="badge ${this._getItemTypeBadgeClass(item.type)}">
                                        ${this._formatItemType(item.type)}
                                      </span>
                                    </td>
                                    <td>
                                      ${
                                        item.type === 'weapon' && item.damageDice
                                          ? html` <span class="small">${item.damageDice} ${item.damageType || ''}</span> `
                                          : item.type === 'armor' && item.acBonus
                                            ? html` <span class="small">AC ${item.acBonus}</span> `
                                            : html` <span class="text-muted">-</span> `
                                      }
                                    </td>
                                    <td>
                                      ${
                                        item.equipped && item.slot
                                          ? html`
                                            <span class="badge bg-success">${this._formatSlotName(item.slot)}</span>
                                          `
                                          : item.equipped
                                            ? html` <span class="badge bg-success">Equipped</span> `
                                            : html` <span class="badge bg-secondary">Inventory</span> `
                                      }
                                    </td>
                                    <td class="text-end">${item.quantity}</td>
                                  </tr>
                                `,
                              )}
                            </tbody>
                          </table>
                        `
                    }
                  </div>
                </div>
              `
              : ''
      }
    `;
  }
}

customElements.define('character-detail-view', CharacterDetailView);
